<template>
  <div class="policy-page-wrapper">
    <WeCircularNavbar @go-home="$emit('go-home')" @go-countries="$emit('go-countries')" />

    <section class="policy-page">
      <div class="policy-container">
        <button class="back-button" type="button" @click="$router.back()">
          ← Back
        </button>

        <h1 class="page-title">
          European Policies and
          <br />
          Strategies
        </h1>

        <h2 class="page-subtitle">
          Circular Economy
        </h2>

        <p class="page-intro">
          Explore how European circular economy policies drive sustainable growth, resource efficiency and innovative
          business models.
        </p>

        <section class="filters-section">
          <div class="filters-grid">
            <div class="filter-field filter-field--search">
              <input v-model.trim="searchQuery" type="text" placeholder="Search" class="filter-input" />
              <span class="filter-icon">⌕</span>
            </div>

            <div class="filter-field">
              <select v-model="selectedTopic" class="filter-select">
                <option value="">Select topic</option>
                <option v-for="topic in topicOptions" :key="topic" :value="topic">
                  {{ topic }}
                </option>
              </select>
            </div>

            <div class="filter-field">
              <select v-model="selectedCategory" class="filter-select">
                <option value="">Select Category</option>
                <option v-for="category in categoryOptions" :key="category" :value="category">
                  {{ category }}
                </option>
              </select>
            </div>
          </div>
        </section>

        <section class="cards-list">
          <article v-for="doc in paginatedDocuments" :key="doc.id" class="policy-card">
            <h3 class="card-title">
              {{ doc.title }}
            </h3>

            <div class="card-meta">
              <div class="meta-item">
                <span class="meta-label">Topic</span>
                <span class="meta-value">{{ doc.topic }}</span>
              </div>

              <div class="meta-item">
                <span class="meta-label">Category</span>
                <span class="meta-value">{{ doc.category }}</span>
              </div>

              <div class="meta-item meta-item--right">
                <span class="meta-label">Website</span>
                <a :href="doc.url" target="_blank" rel="noopener noreferrer" class="meta-link">
                  {{ doc.website }}
                </a>
              </div>
            </div>

            <a :href="doc.url" target="_blank" rel="noopener noreferrer" class="learn-more">
              Learn More
              <span aria-hidden="true">→</span>
            </a>
          </article>

          <div v-if="!paginatedDocuments.length" class="empty-state">
            No documents found for the selected filters.
          </div>
        </section>

        <div v-if="totalPages > 1" class="pagination-dots">
          <button
            v-for="page in totalPages"
            :key="'page-' + page"
            type="button"
            class="dot"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          />
        </div>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'
import { circularEconomyDocuments } from './circularEconomyDocuments'

export default {
  name: 'CircularEconomyPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  emits: ['go-home', 'go-countries'],

  data() {
    return {
      documents: circularEconomyDocuments,
      searchQuery: '',
      selectedTopic: '',
      selectedCategory: '',
      currentPage: 1,
      itemsPerPage: 6
    }
  },

  computed: {
    topicOptions() {
      return [...new Set(this.documents.map(item => item.topic).filter(Boolean))].sort()
    },

    categoryOptions() {
      return [...new Set(this.documents.map(item => item.category).filter(Boolean))].sort()
    },

    filteredDocuments() {
      const query = this.searchQuery.trim().toLowerCase()

      return this.documents.filter(item => {
        const matchesSearch =
          !query ||
          item.title.toLowerCase().includes(query) ||
          item.topic.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query) ||
          item.website.toLowerCase().includes(query)

        const matchesTopic = !this.selectedTopic || item.topic === this.selectedTopic
        const matchesCategory = !this.selectedCategory || item.category === this.selectedCategory

        return matchesSearch && matchesTopic && matchesCategory
      })
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredDocuments.length / this.itemsPerPage))
    },

    paginatedDocuments() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredDocuments.slice(start, end)
    }
  },

  watch: {
    searchQuery() {
      this.currentPage = 1
    },
    selectedTopic() {
      this.currentPage = 1
    },
    selectedCategory() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
.policy-page {
  min-height: 100vh;
  background: #f3f3f3;
  padding: 22px 0 64px;
}

.policy-container {
  width: 100%;
  max-width: 80%;
  margin: 0 auto;
  padding: 0 38px;
  box-sizing: border-box;
}

.back-button {
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  height: 30px;
  min-width: 66px;
  padding: 0 12px;
  margin-bottom: 22px;
  border: 1px solid #003399;
  border-radius: 8px;
  background: transparent;
  color: #003399;
  line-height: 1;
  cursor: pointer;
  margin-left: 0;
}

.page-title {
  margin: 0;
  max-width: 760px;
  color: #003399;
  line-height: 1.06;
  font-weight: 600;
  letter-spacing: -0.03em;
  text-align: left;
}

.page-subtitle {
  margin: 28px 0 0;
  color: #003399;
  line-height: 1.08;
  font-weight: 600;
  text-align: left;
}

.page-intro {
  max-width: 30%;
  margin: 14px 0 34px;
  color: #0e6eb6;
  line-height: 1.5;
  text-align: left;
}

.filters-section {
  margin-bottom: 24px;
}

.filters-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr;
  gap: 10px;
}

.filter-field {
  position: relative;
}

.filter-input,
.filter-select {
  width: 100%;
  height: 30px;
  padding: 0 30px 0 10px;
  border: 1px solid #8fd3cd;
  border-radius: 4px;
  background: transparent;
  color: #6e8b8b;
  line-height: 1;
  box-sizing: border-box;
  outline: none;
}

.filter-input::placeholder {
  color: #9ab0b0;
}

.filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  background-image: linear-gradient(45deg, transparent 50%, #8fd3cd 50%),
    linear-gradient(135deg, #8fd3cd 50%, transparent 50%);
  background-position: calc(100% - 15px) calc(50% - 2px), calc(100% - 10px) calc(50% - 2px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}

.filter-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-52%);
  color: #8fd3cd;
  pointer-events: none;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.policy-card {
  background: #f7f9fc;
  border: 1px solid #cfd9e8;
  border-radius: 8px;
  padding: 12px 14px;
  color: #003399;
  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0 0 10px;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  color: #003399;
  text-align: left;
}

.card-meta {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  align-items: start;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-item--right {
  text-align: left;
}

.meta-label {
  font-size: 16px;
  font-weight: 700;
  color: #0e6eb6;
}

.meta-value,
.meta-link {
  font-size: 16px;
  color: #6c86a8;
  text-decoration: none;
  line-height: 1.3;
}

.meta-link:hover {
  text-decoration: underline;
}

.learn-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 16px;
  color: #18baa8;
  font-weight: 600;
  justify-content: flex-start;
  align-self: flex-start;
}

.learn-more:hover {
  text-decoration: underline;
}

.empty-state {
  padding: 18px 20px;
  border-radius: 8px;
  background: #e8edf4;
  color: #003399;
}

.pagination-dots {
  display: flex;
  justify-content: center;
  gap: 7px;
  margin-top: 18px;
}

.dot {
  width: 11px;
  height: 11px;
  border: none;
  border-radius: 999px;
  background: #bcc6d8;
  padding: 0;
  cursor: pointer;
}

.dot.active {
  background: #18baa8;
}

@media (max-width: 1100px) {
  .policy-container {
    max-width: 92%;
    padding: 0 20px;
  }

  .page-intro {
    max-width: 48%;
  }
}

@media (max-width: 900px) {
  .page-title {
    font-size: 28px;
  }

  .page-subtitle {
    font-size: 22px;
  }

  .page-intro {
    max-width: 100%;
  }

  .filters-grid,
  .card-meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .policy-page {
    padding-bottom: 48px;
  }

  .page-title {
    font-size: 22px;
  }

  .page-subtitle {
    font-size: 18px;
  }

  .page-intro {
    font-size: 12px;
  }

  .policy-card {
    padding: 14px;
  }

  .card-title {
    font-size: 12px;
  }

  .meta-label,
  .meta-value,
  .meta-link,
  .learn-more {
    font-size: 11px;
  }
}
</style>
